import styled from "styled-components";

/* ── Device info surface ──────────────────────────────────────────────── */

export const InfoPage = styled.div`
  width: 100%;
  padding-top: 8px;
`;

/* Media sidebar + content region; wraps (media on top) on narrow screens */
export const InfoLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px 40px;
  align-items: flex-start;
`;

export const Content = styled.div`
  flex: 1 1 480px;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

/* Spesifikasjoner + Informasjon side by side, reflowing on narrow widths */
export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 28px 40px;
  align-items: start;
`;

/* ── Media (device / user image) ──────────────────────────────────────── */

export const Media = styled.div`
  flex: 0 1 300px;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const MediaFrame = styled.div`
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 10px;

  img {
    display: block;
    max-width: 100%;
    max-height: 240px;
    margin: auto;
  }
`;

export const FallbackImg = styled.img`
  max-height: 150px !important;
  opacity: 0.5;
  padding: 24px;
`;

export const MediaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

/* ── Sections ─────────────────────────────────────────────────────────── */

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/* Message thread block within the content region */
export const MessagesBlock = styled.section`
  display: flex;
  flex-direction: column;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid ${props => props.theme.colors.borderColor};
`;

export const MessagesInner = styled.div`
  width: 100%;
  max-width: 560px;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 4px;
  padding-bottom: 12px;
  font-size: ${props => props.theme.font.normal};
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};
`;

/* ── Definition rows (label / value) ──────────────────────────────────── */

export const Dl = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 16px;
  min-height: 44px;
  padding: 8px 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};

  &:last-child {
    border-bottom: none;
  }
`;

/* Full-width row for controls that own the whole line (e.g. checkbox) */
export const StackRow = styled(Row)`
  align-items: flex-start;
  flex-direction: column;
`;

export const Term = styled.span`
  flex: 0 1 auto;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
`;

export const Value = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  text-align: right;
  font-size: ${props => props.theme.font.normal};
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  word-break: break-word;
`;

export const Muted = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  opacity: 0.7;
`;

export const TrainedNote = styled.p`
  margin: 8px 0 0;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
`;

/* ── Connection status chip ───────────────────────────────────────────── */

export const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  font-size: ${props => props.theme.font.small};
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
`;

export const StatusDot = styled.span<{ $connected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props =>
    props.$connected
      ? props.theme.deviceStatus.clear
      : props.theme.colors.textSecondary};
`;

/* ── Buttons & inputs ─────────────────────────────────────────────────── */

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${"currentColor"};
    outline-offset: 2px;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  border: none;
  color: ${props => props.theme.colors.accentContrast};
  background: ${props => props.theme.colors.accent};

  &:hover:not(:disabled) {
    filter: brightness(0.94);
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  color: ${props => props.theme.colors.textPrimary};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.buttonBorder};

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.accent};
  }
`;

export const TextInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 12px;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 4px;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: ${props => props.theme.colors.accent};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
    opacity: 0.7;
  }
`;
